import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  coupleShortName,
  INQUIRY_STATUSES,
  INQUIRY_STATUS_LABELS,
  type InquiryStatus,
} from '@vendorops/shared';
import { client } from '@/lib/api';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import StatusChip from '@/components/StatusChip';
import QuoteField from '@/components/QuoteField';
import QuoteBreakdown from '@/components/QuoteBreakdown';
import QuoteHistory, { type SourceMessage } from '@/components/QuoteHistory';
import UnreadAttachmentNotice from '@/components/UnreadAttachmentNotice';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ConversationThreads from '@/components/ConversationThreads';
import { useGmailStatus } from '@/components/GmailBanner';
import { formatInstant } from '@/lib/date';

/**
 * The server's own words, when it has any.
 *
 * One refusal reaches a planner from this page, and it names the remedy:
 * moving an inquiry to another currency while the figure on screen is a bare
 * reading recorded in a different one answers 409 with "enter the amount in
 * EUR, or clear the quote first". A flat "Failed to update" in its place
 * throws away the only part they can act on.
 *
 * It used to cite a second, "undo that one first", from the unreject endpoint.
 * That endpoint is gone by decision — typing over a figure is the whole
 * override surface and there is no undo — so no route produces that string
 * any more. One message, one helper, and the mutation below is currently the
 * only caller; it stays a helper so a second one cannot drift into a different
 * standard.
 */
async function failure(
  res: { json: () => Promise<unknown> },
  fallback: string,
): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: string };
    return new Error(body.error ?? fallback);
  } catch {
    // A non-JSON body (a proxy's 502 page, a dropped connection) is not a
    // message worth showing; the fallback at least names the action.
    return new Error(fallback);
  }
}

/** The three readings of an inquiry. A union rather than a bare string so a
 *  typo in a `TabsTrigger` value fails the build instead of rendering a panel
 *  that never opens. */
type TabName = 'quote' | 'conversation' | 'history';

export default function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: inquiry, isLoading } = useQuery({
    queryKey: ['inquiry', id],
    queryFn: async () => {
      const res = await client.api.inquiries[':id'].$get({ param: { id: id! } });
      if (!res.ok) throw new Error('Failed to load inquiry');
      return res.json();
    },
    enabled: !!id,
    /**
     * Always refetched, against the app's five-minute default.
     *
     * What this query carries changes without the planner doing anything: a
     * vendor replies, the poller files it, the status advances and a quote
     * appears. Five minutes of cache on that meant opening the page shortly
     * after a quote arrived showed the page as it was *before* it arrived,
     * with no indication anything was being withheld — which reads as the
     * extraction having failed rather than as a stale read.
     *
     * The conversation below keeps the default on purpose: its bodies are
     * live Gmail reads and cost quota. This one is a database row.
     */
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const { data: threadData, isLoading: threadsLoading } = useQuery({
    queryKey: ['inquiry-threads', id],
    queryFn: async () => {
      const res = await client.api.inquiries[':id'].threads.$get({ param: { id: id! } });
      if (!res.ok) throw new Error('Failed to load the conversation');
      return res.json();
    },
    enabled: !!id,
    // The newest thread's bodies are live Gmail reads. Re-fetching them on
    // every focus would spend quota to redisplay text that has not changed.
    staleTime: 5 * 60 * 1000,
  });

  const { data: gmail } = useGmailStatus();
  const threads = threadData?.threads ?? [];

  /**
   * Which reading is open.
   *
   * The conversation, always, until the planner says otherwise. It is the
   * spine of an inquiry and the thing that explains every other part of the
   * page — including a quote, which is a reading *of* a message in it. A
   * default that moved with the data would also mean the page a planner
   * opened yesterday and the page they open today are different pages, for a
   * reason they never chose.
   *
   * Following a quote back to the sentence it came from crosses a tab
   * boundary, so `onEvidenceClick` moves this as well as the thread —
   * otherwise the highlight lands on a panel nobody is looking at.
   */
  const [tab, setTab] = useState<TabName>('conversation');

  /** Shown on the tab itself: the number of options waiting, which is the
   *  whole reason to open it. */
  /**
   * What the badge counts: quotes, matching the tab's name.
   *
   * It counted packages while the panel showed one quote, which read as "3"
   * for a vendor who had sent one proposal with three tiers — and would have
   * gone on reading "3" after a second proposal arrived, which is the
   * opposite of the thing worth noticing.
   */
  const quoteCount = inquiry?.quotes?.length ?? 0;

  /**
   * The document the packages were read out of, when they were read out of
   * one. `sourceKind` is not served, so this reads provenance the way the
   * planner would: the message the quote came from, and what was attached to
   * it. A body quote on a message that also carries a brochure would name the
   * brochure wrongly — which is the honest limit of what is on the wire, and
   * the reason this says "read from" rather than "extracted from".
   */
  const quoteHistory = inquiry?.quotes ?? [];

  /**
   * How to reach the original behind each quote.
   *
   * Built here because the href needs the inquiry id, and keyed by message so
   * two quotes read from one email — a stated price and its attachment —
   * share one entry rather than each computing their own.
   *
   * The attachment is addressed by *position*: `attachmentMeta` carries no
   * Gmail id, and the route re-reads the live part list and indexes into it,
   * so the number here and the number there mean the same thing.
   */
  const quoteSources: Record<string, SourceMessage> = {};
  for (const message of inquiry?.messages ?? []) {
    // `attachmentMeta` is jsonb, so it arrives as `unknown` and is narrowed
    // here rather than trusted: a row written before the column existed holds
    // null, and one written by an older ingest holds a shape this predates.
    const meta = Array.isArray(message.attachmentMeta) ? message.attachmentMeta : [];
    // The document, not merely the first file: a vendor who attaches their
    // logo and their proposal should not have the logo opened.
    const index = meta.findIndex(
      (a) => (a as { mimeType?: unknown } | null)?.mimeType === 'application/pdf',
    );
    const named = index >= 0 ? (meta[index] as { filename?: unknown }) : undefined;
    quoteSources[message.id] = {
      attachmentIndex: index >= 0 ? index : null,
      attachmentName: typeof named?.filename === 'string' ? named.filename : null,
      attachmentHref:
        index >= 0
          ? `/api/inquiries/${inquiry!.id}/messages/${message.id}/attachment/${index}`
          : null,
    };
  }

  /**
   * `undefined` is "the planner has not chosen yet", which is not the same as
   * "the planner closed everything" — so the default falls out of the data with
   * no effect to synchronise, and collapsing the last open thread still sticks.
   */
  const [chosenThreadId, setChosenThreadId] = useState<string | null | undefined>(undefined);
  const expandedThreadId =
    chosenThreadId === undefined ? (threads[0]?.gmailThreadId ?? null) : chosenThreadId;

  /** Set when the planner follows the quote's provenance line, so the message
   *  it was read from can highlight the sentence carrying the figure. */
  const [highlight, setHighlight] = useState<{ messageId: string; text: string } | null>(null);

  /**
   * Attachments no extraction opened. Empty for almost every inquiry.
   *
   * Served rather than derived: whether a file was readable is a fact about
   * the reading that happened at ingest, and recomputing it here from a size
   * and today's ceiling would answer a different question — what we *would*
   * skip now — and could not see a download that simply failed.
   */
  const unreadAttachments = inquiry?.unreadAttachments ?? [];

  /**
   * Open the message behind a reading, from wherever the planner clicked.
   *
   * Shared by the quote history's provenance line and the unread-attachment
   * notice, because they are the same intent — "show me the email this came
   * from" — and two copies would be free to disagree about whether the tab
   * switches or the thread expands.
   */
  function openMessage(messageId: string) {
    const message = inquiry?.messages.find((m) => m.id === messageId);
    if (!message) return;
    setTab('conversation');
    setChosenThreadId(message.gmailThreadId);
    setHighlight({ messageId, text: '' });
  }

  const update = useMutation({
    /**
     * `quoteAmount: null` is a rejection, not a blank. The server strikes out
     * whichever reading produced the figure that was on screen, so the next
     * resolve cannot hand the same number straight back — which it did, in the
     * very response to this mutation, while a clear was only a clear.
     *
     * One layer at a time, which is what makes two clears a sequence rather
     * than a repeat: the first drops the planner's own figure when they typed
     * one and reveals the vendor's reading underneath, the second strikes that
     * out. Nothing distinguishes the two outcomes here — the response carries
     * no flag for it and there is no undo — so the refetched `quote` below is
     * the whole feedback, and the figure changing or disappearing is how the
     * planner reads which happened.
     *
     * `quoteCurrency` on its own can come back 409 when the figure on screen
     * is a bare reading recorded in another currency. `failure` surfaces the
     * server's own message, which names both ways out, so that path needs
     * nothing here.
     */
    mutationFn: async (patch: {
      status?: InquiryStatus;
      quoteAmount?: number | null;
      quoteCurrency?: string;
    }) => {
      const res = await client.api.inquiries[':id'].$put({ param: { id: id! }, json: patch });
      if (!res.ok) throw await failure(res, 'Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['wedding'] });
      toast.success('Updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!inquiry) return <p className="text-sm text-muted-foreground">Inquiry not found.</p>;

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Link
          to={`/weddings/${inquiry.weddingId}`}
          className="label-caps text-xs text-muted-foreground hover:text-foreground"
        >
          ← {coupleShortName(inquiry.wedding)}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="display-tight text-3xl font-semibold">{inquiry.vendor.businessName}</h1>
            <p className="text-sm text-muted-foreground">
              {inquiry.category}
              {inquiry.sentAt && ` · inquired ${formatInstant(inquiry.sentAt, { month: 'short', day: 'numeric', year: 'numeric' })}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusChip
              status={inquiry.status as InquiryStatus}
              source={inquiry.statusSource as 'system' | 'user'}
            />
            <Select
              value={inquiry.status}
              onValueChange={(v) => update.mutate({ status: v as InquiryStatus })}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INQUIRY_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {INQUIRY_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Reads as the third line of the header rather than a panel of its own:
          the quote is a property of this inquiry, and the planner opened the
          page to see it. A Card here would have made it an announcement. */}
      <QuoteField
        quote={inquiry.quote}
        /* 0 where there is no quote: the caption only reports on a span, and
           a span implies a tree. */
        packageCount={inquiry.quote?.packageCount ?? 0}
        saving={update.isPending}
        onChange={(amount) => update.mutate({ quoteAmount: amount })}
      />

      {inquiry.sendState === 'failed' && (
        <Card className="rounded-3xl bg-destructive/10 p-5">
          <p className="text-sm text-destructive">
            This one didn't send: {inquiry.sendError}
          </p>
        </Card>
      )}

      {/* Three readings of one inquiry, and a planner is only ever doing one
          of them: comparing what the vendor offered, reading what was said, or
          asking why the status moved. Stacked they made the first the hardest
          to reach — a three-package quote sat below a thread that grows every
          time anyone replies. The quote leads because it is what the page is
          usually opened for; `sourceMessageId` still switches to the
          conversation, so following a number back to its sentence works
          across the tabs rather than in spite of them. */}
      <Tabs
        value={tab}
        onValueChange={(next) => setTab(next as TabName)}
        className="gap-4"
      >
        <TabsList variant="line" className="h-auto gap-4 p-0">
          {/* The conversation leads. It is the spine of an inquiry — every
              inquiry has one from the moment it is sent, where a quote
              arrives late if at all — and it is what a planner scans to
              understand where things stand. */}
          <TabsTrigger value="conversation" className="flex-none px-0 pb-2">
            Conversation
          </TabsTrigger>
          {/* "Quotes", plural, and deliberately not "Quote": the headline
              field above is labelled Quote, where this is every quote the
              vendor has sent. The plural is what keeps the two apart, for a
              screen reader as much as on screen. */}
          <TabsTrigger value="quote" className="flex-none px-0 pb-2">
            Quotes
            {/* Only once there is more than one. A badge reading "1" on a tab
                called Quotes is a decoration. */}
            {quoteCount > 1 && (
              <span className="ml-1.5 rounded-full bg-[var(--color-primary-container)] px-1.5 py-0.5 text-[0.7rem] font-medium text-[var(--color-accent-foreground)]">
                {quoteCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-none px-0 pb-2">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="quote" className="space-y-3">
          {/* Above the readings, not among them. An attachment nobody opened
              is a statement about how complete the list below is, so it has
              to be legible before the list is read rather than after. */}
          <UnreadAttachmentNotice
            attachments={unreadAttachments}
            onOpenMessage={openMessage}
          />

          {quoteHistory.length > 0 ? (
            <QuoteHistory
              entries={quoteHistory}
              currency={inquiry.quote?.currency ?? 'USD'}
              sources={quoteSources}
              onOpenMessage={openMessage}
            />
          ) : inquiry.quote ? (
            // A headline with no tree behind it: a figure the planner typed,
            // or one read out of a sentence before the structured extractor
            // existed. There is no history to sequence, only the one reading.
            <QuoteBreakdown quote={inquiry.quote} />
          ) : (
            <Card className="rounded-3xl bg-card p-5 shadow-ambient">
              {/* The promise is conditional on our having kept it. With an
                  unread attachment sitting above this card, "their packages
                  land here" is the sentence that sent a planner off to
                  retype a figure we already had the file for. */}
              <p className="text-sm text-muted-foreground">
                {unreadAttachments.length > 0
                  ? `No quote read yet — and the attachment above was not one of the things read.`
                  : `No quote yet. When ${inquiry.vendor.businessName} sends one — in the message or as an attachment — their packages land here.`}
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="conversation">
      {/* Deliberately not one Card around everything: each thread is its own
          card, so two conversations with the same vendor cannot melt into one.
          No subject line here either — each thread carries its own, and a
          vendor with three has no single subject this could honestly show. */}
      <div className="space-y-3">
        {threadsLoading ? (
          <Card className="rounded-3xl bg-card p-5 shadow-ambient">
            <p className="text-sm text-muted-foreground">Loading the conversation…</p>
          </Card>
        ) : threads.length === 0 ? (
          <Card className="rounded-3xl bg-card p-5 shadow-ambient">
            {/* The composer lives at the end of a thread, so with no thread
                there is no disabled box to explain — only this. Account-level
                trouble is deliberately not named here; GmailBanner already
                carries it, with the remedy, above every page. */}
            <p className="text-sm text-muted-foreground">
              {inquiry.sendState === 'queued'
                ? 'Queued to send shortly. You can reply once it has gone out.'
                : 'Nothing yet.'}
            </p>
          </Card>
        ) : (
          <ConversationThreads
            inquiryId={inquiry.id}
            threads={threads}
            expandedThreadId={expandedThreadId}
            onToggle={(threadId) =>
              setChosenThreadId((current) => {
                const open = current === undefined ? (threads[0]?.gmailThreadId ?? null) : current;
                return open === threadId ? null : threadId;
              })
            }
            gmailAddress={gmail?.connected ? gmail.email : null}
            highlight={highlight}
          />
        )}
      </div>
        </TabsContent>

        {/* ── Status history (PRD §F6) ────────────────────────────────────── */}
        <TabsContent value="history">
      <Card className="rounded-3xl bg-card p-5 shadow-ambient">
        <div className="space-y-3">
          {inquiry.timeline.map((event) => (
            <div key={event.id} className="flex items-start gap-3 text-sm">
              <span
                className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
                  event.actor === 'user' ? 'bg-primary' : 'bg-muted-foreground'
                }`}
              />
              <div>
                <p>
                  {event.fromStatus
                    ? `${INQUIRY_STATUS_LABELS[event.fromStatus as InquiryStatus]} → `
                    : ''}
                  <strong>{INQUIRY_STATUS_LABELS[event.toStatus as InquiryStatus]}</strong>
                </p>
                <p className="text-xs text-muted-foreground">
                  {event.rationale} ·{' '}
                  {formatInstant(event.createdAt, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
